import { z } from "zod";

const musicAccessFlowAlbumSchema = z.object({
  id: z.string(),
  title: z.string(),
  image: z.string(),
  trackNumber: z.number(),
});

export const musicAccessFlowSongSchema = z.object({
  id: z.string(),
  albums: z.array(musicAccessFlowAlbumSchema),
  title: z.string(),
  previewUrl: z.string().nullable(),
  duration: z.number().nonnegative(),
  isSetList: z.boolean().default(false),
});

export const musicAccessFlowCollaboratorSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  slug: z.string().nullable().optional(),
  /** Whether the artist has a published site (the web's wire says). */
  hasSite: z.boolean().optional(),
});

export const musicAccessFlowRecommendationSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  artistName: z.string(),
  image: z.string().nullable(),
  isSetList: z.boolean().default(false),
  /** Whether its artist has a published site (the web's wire says). */
  hasSite: z.boolean().optional(),
});

/**
 * The platform-neutral subset of a music access required by the unlock flow.
 * Both the mobile access wire and the future LIB viewer endpoint satisfy this
 * contract; platform-only fields stay outside the shared interface.
 */
export const musicAccessFlowAccessSchema = z.object({
  id: z.string(),
  artistId: z.string(),
  title: z.string(),
  artistName: z.string(),
  artistImage: z.string().nullable(),
  extLinks: z.array(z.string()).nullable(),
  extSource: z.string().nullable(),
  unlocked: z.boolean(),
  /** Members-only music: the artist's fan club's current members own it and
   *  nobody unlocks it. Absent where the wire does not carry it yet. */
  membersOnly: z.boolean().optional(),
  /** Whose fan club it is for: its owner artist, who on a collaborator's
   *  page is not `artistName`. */
  clubArtistName: z.string().nullable().optional(),
  price: z.number().nonnegative(),
  priceMin: z.number().nonnegative().nullable(),
  priceMax: z.number().nonnegative().nullable(),
  priceCurrency: z.string(),
  songs: z.array(musicAccessFlowSongSchema).min(1),
  collaborators: z.array(musicAccessFlowCollaboratorSchema).optional(),
  recommendations: z.array(musicAccessFlowRecommendationSchema).optional(),
});

export const musicAccessFlowScreenSchema = z.enum(["details", "checkout"]);

export type MusicAccessFlowAccess = z.infer<typeof musicAccessFlowAccessSchema>;
export type MusicAccessFlowSong = z.infer<typeof musicAccessFlowSongSchema>;
export type MusicAccessFlowCollaborator = z.infer<
  typeof musicAccessFlowCollaboratorSchema
>;
export type MusicAccessFlowRecommendation = z.infer<
  typeof musicAccessFlowRecommendationSchema
>;
export type MusicAccessFlowScreen = z.infer<typeof musicAccessFlowScreenSchema>;
